import {
  AuthenticationResult,
  InteractionRequiredAuthError,
  IPublicClientApplication,
  PublicClientApplication,
  SilentRequest,
} from "@azure/msal-browser";
import { ApiScope, msalConfig } from "@config/msal";

export const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Get access token for API calls with automatic refresh.
 * Falls back to interactive popup if silent acquisition fails.
 *
 * @returns AuthenticationResult with accessToken and account info
 * @throws Error if token acquisition fails
 *
 */

export const getMicrosoftToken = async (): Promise<AuthenticationResult> => {
  const request: SilentRequest = {
    scopes: [ApiScope],
  };

  try {
    return await msalInstance.acquireTokenSilent(request);
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      console.log('Silent "acquire token" failed, falling back to popup', error);
      return await msalInstance.acquireTokenPopup(request);
    }
    throw error;
  }
};

/**
 * Authenticate user with Microsoft SSO using popup flow.
 * Requests OpenID scope and API access, then sets the active account.
 *
 * @param instance - MSAL PublicClientApplication instance
 * @returns AuthenticationResult with accessToken and account info
 * @throws Error if login fails
 */
export const loginWithPopup = async (
  instance: IPublicClientApplication
): Promise<AuthenticationResult> => {
  const result = await instance.loginPopup({
    scopes: ["openid"],
    extraScopesToConsent: [ApiScope],
  });

  instance.setActiveAccount(result.account);
  return result;
};

export const isMsalError = (error: unknown, errorCode: string): boolean => {
  return (
    typeof error === "object" &&
    error !== null &&
    "errorCode" in error &&
    error.errorCode === errorCode
  );
};

export const getMsalErrorMessage = (error: unknown): string => {
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    return error.message;
  }
  return "Erreur de connexion inconnue";
};
